import { FC } from 'react'
import { Button, H2, H3 } from '@/common/ui'
import useBundleTrade from './useBundleTrade'
import { COMMON_COPY, ORDER_CONFIRMATION_COPY, ORDER_FORM_COPY } from '@/locales/en'
import { AlertCircle, AlertTriangle, Check, Gift } from 'lucide-react'
import { ReviewItem, ReviewItems } from './reviewItems'
import { SubmissionStatus } from './types'
import { Alert } from '@/common/ui/alert'
import { SecuritySummaryItem } from './SecuritySummaryItem'
import Money from '@/common/models/money'
import { OrderRewardItem } from '@/trading/orderRewardItem'

type Props = {
    onClose?: () => void
}

type ButtonProps = {
    onClose: () => void
}

export const OrderConfirmationButtons: FC<ButtonProps> = ({ onClose }) => {
    return (
        <div className="w-full pt-1">
            <Button onClick={onClose} className="mx-auto block mt-8" size="lg" variant="secondary">
                {COMMON_COPY.close}
            </Button>
        </div>
    )
}

export const OrderConfirmation: FC<Props> = ({ onClose }) => {
    const { orderItems, orderType, name, promotions } = useBundleTrade()

    if (!orderItems || orderItems.length === 0) return null

    if (orderItems.every((item) => item.submissionStatus === SubmissionStatus.SUCCESS || item.units === 0)) {
        return (
            <div className="w-full self-stretch inline-flex flex-col justify-start items-center gap-4">
                <span className="w-14 h-14 bg-secondary rounded-full mx-auto flex justify-center items-center">
                    <Check />
                </span>
                <H2 className="text-center pt-4">{ORDER_CONFIRMATION_COPY.thankYouTitle.replace('{}', 'buy')}</H2>
                <H3 className="text-center pt-6 pb-2">{ORDER_CONFIRMATION_COPY.summary}</H3>
                <ReviewItems />

                {onClose && <OrderConfirmationButtons onClose={onClose} />}
            </div>
        )
    }

    const activeOrderItems = orderItems.filter((s) => s.units > 0)
    const failedOrderItems = orderItems.filter((s) => s.submissionStatus === SubmissionStatus.FAILED)
    const successfulOrderItems = orderItems.filter((s) => s.submissionStatus === SubmissionStatus.SUCCESS)
    const estimatedAmount = successfulOrderItems.reduce((acc, item) => acc + item.amount, 0)
    const brokerage = successfulOrderItems.length * 9.9
    const total = estimatedAmount + brokerage

    return (
        <div className="w-full self-stretch inline-flex flex-col justify-start items-center gap-4">
            <span className="w-14 h-14 bg-yellow-200 rounded-full mx-auto flex justify-center items-center">
                <AlertTriangle />
            </span>
            <H2 className="text-center pt-4">{ORDER_CONFIRMATION_COPY.failedTitle}</H2>
            <ul className="self-stretch flex flex-col justify-start items-start">
                <ReviewItem label={ORDER_FORM_COPY.bundle} value={name ?? ''} />
                <ReviewItem
                    label={ORDER_FORM_COPY.securities}
                    value={activeOrderItems.map((s) => s.securityCode).join(', ')}
                />
                <ReviewItem
                    label={ORDER_FORM_COPY.orderType}
                    value={ORDER_FORM_COPY.orderTypeMapping[orderType as keyof typeof ORDER_FORM_COPY.orderTypeMapping]}
                />
                <li className="self-stretch py-4 border-b border-gray-200 inline-flex flex-col justify-start items-start gap-4">
                    <div className="text-center justify-start text-black text-sm font-normal font-['Inter'] leading-5">
                        {ORDER_CONFIRMATION_COPY.failedOrders}
                    </div>
                    <Alert variant={'destructive'} className="w-full p-4">
                        <div className="flex-1 flex justify-start items-start gap-4">
                            <AlertCircle className="h-5 w-5 text-red-500" />
                            <div className="self-stretch justify-center text-sentiment-bad text-sm font-normal font-['Inter'] leading-5">
                                {ORDER_CONFIRMATION_COPY.failedOrdersAlert}
                            </div>
                        </div>
                    </Alert>
                    <div className="self-stretch flex flex-col justify-start items-start gap-4">
                        {failedOrderItems.map((s) => (
                            <div
                                key={s.securityCode}
                                className="self-stretch p-2 bg-stone-50 rounded-lg inline-flex justify-between items-start overflow-hidden outline outline-1 outline-sentiment-bad outline-offset-[-1px]"
                            >
                                <SecuritySummaryItem
                                    securityCode={s.securityCode}
                                    weight={s.weight}
                                    amount={0}
                                    units={0}
                                    relevantPrice={0}
                                />
                            </div>
                        ))}
                    </div>
                </li>
                <li className="self-stretch py-4 border-b border-gray-200 flex flex-col justify-start items-start gap-2">
                    <div className="text-center justify-start text-black text-sm font-normal font-['Inter'] leading-5">
                        {ORDER_CONFIRMATION_COPY.submittedOrders}
                    </div>
                    <div className="self-stretch flex flex-col justify-start items-start gap-4">
                        {successfulOrderItems.map((s) => (
                            <div
                                key={s.securityCode}
                                className="self-stretch p-2 bg-stone-50 rounded-lg inline-flex justify-between items-start overflow-hidden"
                            >
                                <SecuritySummaryItem
                                    securityCode={s.securityCode}
                                    weight={s.weight}
                                    amount={s.amount}
                                    units={s.units}
                                    relevantPrice={s.relevantPrice}
                                />
                            </div>
                        ))}
                    </div>
                </li>
                <ReviewItem label={ORDER_FORM_COPY.estimatedAmount} value={Money.of(estimatedAmount).toString()} />
                <li
                    className={`self-stretch py-4 inline-flex justify-between items-center ${!promotions || promotions.length === 0 ? 'border-b border-gray-200' : 'pb-0'}`}
                >
                    <div className="justify-start text-black text-sm font-normal font-['Inter'] leading-5">
                        {ORDER_FORM_COPY.estimateBrokerage}
                    </div>
                    <strong className="justify-start text-black text-base font-['Inter'] leading-6">
                        {Money.of(brokerage).toString()}
                    </strong>
                </li>
                {promotions && promotions.length > 0 && (
                    <li className="self-stretch py-4 border-b border-gray-200 inline-flex justify-between items-center">
                        <OrderRewardItem
                            icon={<Gift size={20} />}
                            title={ORDER_FORM_COPY.freeTradeItem.title}
                            description={ORDER_FORM_COPY.freeTradeItem.description}
                        />
                    </li>
                )}
                <ReviewItem label={ORDER_FORM_COPY.total} value={Money.of(total).toString()} />
            </ul>
        </div>
    )
}
